import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "../App.css";
import "../styles/common.css";
import "./Login.css";
import { api } from "../api/axios";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 11) return "Guten Morgen!";
    if (hour < 18) return "Guten Tag!";
    return "Guten Abend!";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const res = await api.post("/auth/login", { username, password });
      const data = res.data || {};

      const accessToken = data.accessToken || data.token;

      if (res.status === 200 && accessToken) {
        await login(accessToken);
        try {
          if (data.user.role === "apotheke") {
            const redirectTo =
              location.state?.from?.pathname || "/pickup-basket";
            navigate(redirectTo, { replace: true });
          } else if (data.user.role === "abpacker") {
            const redirectTo = location.state?.from?.pathname || "/abpacken";
            navigate(redirectTo, { replace: true });
          } else {
            const redirectTo = location.state?.from?.pathname || "/uebersicht";
            navigate(redirectTo, { replace: true });
          }
        } catch {
          setError(data?.message || data?.error || "Anmeldung fehlgeschlagen.");
        }
      }
    } catch (e) {
      const serverData = e?.response?.data;
      setError(
        serverData?.message || serverData?.error || "Anmeldung fehlgeschlagen.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__backdrop" aria-hidden />
      <div className="login-page__orbs" aria-hidden>
        <span className="login-page__orb login-page__orb--1" />
        <span className="login-page__orb login-page__orb--2" />
        <span className="login-page__orb login-page__orb--3" />
      </div>

      <div className="login-card">
        <header className="login-card__header">
          <img
            src={import.meta.env.VITE_FRONTEND_URL + "/logo.png"}
            alt="Leaflytics Logo"
            className="login-card__logo"
          />
          <h1 className="login-card__title">{greeting}</h1>
          <p className="login-card__subtitle">
            Melden Sie sich an, um fortzufahren.
          </p>
        </header>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-field login-field--animate" htmlFor="login-username">
            <span className="login-field__label">Benutzername</span>
            <div className="login-field__control">
              <i className="pi pi-user login-field__icon" aria-hidden />
              <input
                id="login-username"
                className="login-field__input"
                type="text"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Benutzername eingeben"
                autoComplete="username"
                autoFocus
                required
              />
            </div>
          </label>

          <label className="login-field login-field--animate login-field--delay-1" htmlFor="login-password">
            <span className="login-field__label">Passwort</span>
            <div className="login-field__control login-field__control--password">
              <i className="pi pi-lock login-field__icon" aria-hidden />
              <input
                id="login-password"
                className="login-field__input"
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passwort eingeben"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="login-field__toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={
                  showPassword ? "Passwort verbergen" : "Passwort anzeigen"
                }
                title={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
              >
                <i
                  className={showPassword ? "pi pi-eye-slash" : "pi pi-eye"}
                  aria-hidden
                />
              </button>
            </div>
          </label>

          <button
            className="btn btn-primary login-form__submit login-field--animate login-field--delay-2"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="pi pi-spin pi-spinner login-form__submit-icon" aria-hidden />
                Wird angemeldet…
              </>
            ) : (
              "Anmelden"
            )}
          </button>
        </form>

        {error && (
          <div className="login-form__error" role="alert">
            <i className="pi pi-exclamation-circle" aria-hidden />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
